import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import {
  addToWishlist,
  removeFromWishlist,
  loadWishlistIds,
  subscribeWishlistIds,
} from "../../../utils/wishlist";

const HomeProductCard = ({ product, category, view_all }) => {
  const navigate = useNavigate();

  // Wishlist heart. The saved-ids set is loaded once and shared by every card
  // (utils/wishlist), so a page of cards doesn't fetch the wishlist per card.
  const id = String(product?._id || "");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    loadWishlistIds().then((ids) => alive && setSaved(ids.has(id)));
    const unsubscribe = subscribeWishlistIds((ids) => alive && setSaved(ids.has(id)));
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [id]);

  const toggleSaved = async (e) => {
    e.stopPropagation(); // the card itself opens the product
    e.preventDefault();
    if (busy || !id) return;
    setBusy(true);
    setSaved(!saved); // optimistic; the shared set confirms it
    try {
      if (saved) await removeFromWishlist(id);
      else await addToWishlist(id);
    } catch (err) {
      setSaved(saved); // revert on failure
    } finally {
      setBusy(false);
    }
  };

  const img = Array.isArray(product?.images)
    ? product.images[0]
    : product?.images;

  const price = Number(product?.price || 0);
  const discounted = Number(product?.discountedPrice || 0);
  const percent = Number(product?.discountPercent || 0);
  const hasDiscount = price > discounted && discounted > 0;

  return (
    <div
      onClick={() =>
        navigate(`/product/${product._id}/${category}?view_all=${view_all}`)
      }
      className="mx-1 flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-[#eadcc0] bg-white shadow-sm"
    >
      {/* Image — the tinted box shows when a product has no image (no fallback) */}
      <div className="relative aspect-[3/4] overflow-hidden bg-[#f3ece0]">
        {img && (
          <img
            className="h-full w-full object-cover object-top"
            src={img}
            alt={product?.title}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        {percent > 0 && (
          // Compact on phones: at full size the badge and heart covered the
          // top of the photo (often the model's face) on narrow cards.
          <span className="absolute left-2 top-2 rounded-full bg-[#5e3e3e]/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#f1e7da] shadow sm:left-3 sm:top-3 sm:px-2.5 sm:py-1 sm:text-[11px]">
            {percent}% Off
          </span>
        )}
        <button
          type="button"
          onClick={toggleSaved}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
          aria-pressed={saved}
          title={saved ? "Remove from wishlist" : "Add to wishlist"}
          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/85 shadow-sm sm:right-2.5 sm:top-2.5 sm:h-9 sm:w-9"
          style={{ color: saved ? "#b85c5c" : "#5e3e3e" }}
        >
          {saved ? (
            <FavoriteRoundedIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          ) : (
            <FavoriteBorderRoundedIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
          )}
        </button>
      </div>

      {/* Info — min-h so a price row that wraps on a narrow card can't make it
          taller than its neighbours. flex-1 lets it absorb the slack in grids. */}
      <div className="flex flex-1 flex-col p-4">
        {/* Non-breaking space when there's no brand: an empty <p> collapses to
            zero height and that card would sit shorter than the rest. */}
        {/* One line, like the title: a long brand ("TOMMY HILFIGER FIGURE")
            wrapped to two lines and made that card taller than its row. */}
        <p
          className="truncate text-[11px] font-semibold uppercase tracking-wide text-[#b28585]"
          title={product?.brand || undefined}
        >
          {product?.brand || "\u00A0"}
        </p>
        <h3
          className="mt-0.5 truncate text-sm font-medium text-[#511d00]"
          title={product?.title || undefined}
        >
          {product?.title || "\u00A0"}
        </h3>
        {/* Two parts only. The discount percentage is already shown as a pill on
            the image above, so repeating it here was both redundant and the
            reason the row needed a third line on a ~150px mobile card — which
            is what made card heights diverge.
            flex-wrap is kept as a safety net for very long prices; min-h above
            means wrapping no longer changes the card's height. */}
        <div className="mt-2 flex min-h-[3rem] flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="whitespace-nowrap text-base font-bold text-[#511d00]">
            ₹{discounted.toLocaleString("en-IN")}
          </span>
          {hasDiscount && (
            <span className="whitespace-nowrap text-sm text-[#b3a3a3] line-through">
              ₹{price.toLocaleString("en-IN")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeProductCard;
